import { create } from 'zustand'
import { flagsApi } from '@/features/flags/flags.api'
import type { FeatureFlagStatus } from '@/types/models'

/** The flags the client knows about, with the same defaults as `feature_flag.dart` when the API fails. */
export const APP_FEATURES = {
  kycBanking: 'kyc_banking',
  kycIdentity: 'kyc_identity',
  kycDocument: 'enable_document_kyc',
  kycAddress: 'kyc_address',
  onlinePayment: 'enable_online_payment',
  upiDirect: 'enable_upi_direct',
  upiDirectTest: 'enable_upi_direct_test',
  referrals: 'referrals',
  promoCodes: 'promo_codes',
} as const

export type AppFeatureKey = (typeof APP_FEATURES)[keyof typeof APP_FEATURES]

export const DEFAULT_FLAGS: Record<AppFeatureKey, FeatureFlagStatus> = {
  kyc_banking: 'enabled',
  kyc_identity: 'comingSoon',
  enable_document_kyc: 'enabled',
  kyc_address: 'disabled',
  enable_online_payment: 'enabled',
  enable_upi_direct: 'enabled',
  enable_upi_direct_test: 'disabled',
  // LP-998 / LP-1005: dark until the launch offers go live.
  referrals: 'disabled',
  promo_codes: 'disabled',
}

const KNOWN: FeatureFlagStatus[] = ['enabled', 'disabled', 'comingSoon', 'deprecated']

interface FlagsState {
  flags: Record<string, FeatureFlagStatus>
  loaded: boolean
  /** Fetches the flags; on any failure the defaults apply, like the mobile app. Never throws. */
  load: () => Promise<void>
  clear: () => void
  statusOf: (key: string) => FeatureFlagStatus
}

export const useFlagsStore = create<FlagsState>((set, get) => ({
  flags: {},
  loaded: false,
  load: async () => {
    try {
      const list = await flagsApi.list()
      const flags: Record<string, FeatureFlagStatus> = { ...DEFAULT_FLAGS }
      for (const f of list) flags[f.key] = KNOWN.includes(f.status) ? f.status : 'disabled'
      set({ flags, loaded: true })
    } catch {
      set({ flags: { ...DEFAULT_FLAGS }, loaded: true })
    }
  },
  clear: () => set({ flags: {}, loaded: false }),
  statusOf: (key) => get().flags[key] ?? 'disabled',
}))
